import React from 'react';
import { useAppSelector } from '../../store';
import { useSessionTimeout } from '../../hooks/useSessionTimeout';
import SessionTimeoutWarning from './SessionTimeoutWarning';

/**
 * Global session timeout provider.
 *
 * Renders the idle / absolute-duration countdown warning dialog and
 * performs auto-logout when the configured thresholds are exceeded.
 *
 * Must be placed inside the Redux <Provider> and the React-Router context
 * so that `useAppSelector` and `logoutAsync` work correctly.
 *
 * Only activates when the user is authenticated (checked via Redux state).
 */
const SessionTimeoutProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);

  if (!isAuthenticated) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>;
  }

  return <AuthenticatedSessionTimeout>{children}</AuthenticatedSessionTimeout>;
};

/** Inner component -- only mounted when isAuthenticated === true so hooks run. */
const AuthenticatedSessionTimeout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isWarningVisible, timeRemaining, warningSource, extendSession, forceLogout } =
    useSessionTimeout();

  return (
    <>
      {children}
      <SessionTimeoutWarning
        isVisible={isWarningVisible}
        timeRemaining={timeRemaining}
        warningSource={warningSource}
        onExtendSession={extendSession}
        onLogoutNow={forceLogout}
      />
    </>
  );
};

export default SessionTimeoutProvider;
